import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { X, Download, ArrowUpRight, Info, Layers } from "lucide-react";
import api from "@/lib/apiClient";
import { abs, ISSUE_ICONS, RECORD_META, STATUS_TONE, fmtDate } from "@/lib/issues";
import { AiBadge, IssueVisual } from "@/components/issues/IssueCard";
import IssueShare from "@/components/issues/IssueShare";

const iconBtn = "rounded-full border border-npst-line bg-white p-2 text-npst-navy transition-colors hover:border-npst-gold hover:text-npst-gold";

function RecordTile({ r, i }) {
  const meta = RECORD_META[r.type] || RECORD_META.representation;
  const Icon = meta.icon;
  const key = `${r.type}-${i}`;
  const open = r.external
    ? <a href={r.open_url} target="_blank" rel="noopener noreferrer" data-testid={`issue-record-open-button-${key}`} className={iconBtn} aria-label="Open"><ArrowUpRight size={14} /></a>
    : <Link to={r.open_url} data-testid={`issue-record-open-button-${key}`} className={iconBtn} aria-label="Open"><ArrowUpRight size={14} /></Link>;
  return (
    <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.15 + i * 0.04 }}
      className="hover-lift flex items-center gap-3 rounded-xl border border-npst-line bg-white p-3" data-testid="issue-record-tile">
      <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg border border-npst-goldBorder bg-npst-goldLight text-npst-gold"><Icon size={18} /></div>
      <div className="min-w-0 flex-1">
        <div className="font-mono text-[9px] uppercase tracking-[0.18em] text-npst-gold">{meta.label}{r.date ? ` · ${fmtDate(r.date)}` : ""}</div>
        <div className="truncate text-sm text-npst-ink" title={r.title}>{r.title}</div>
      </div>
      <div className="flex shrink-0 gap-1.5">
        {r.download_url && <a href={abs(r.download_url)} target="_blank" rel="noopener noreferrer" download data-testid={`issue-record-download-button-${key}`} className="rounded-full bg-npst-navy p-2 text-white transition-colors hover:bg-npst-gold" aria-label="Download"><Download size={14} /></a>}
        {r.open_url && open}
      </div>
    </motion.div>
  );
}

function Progress({ steps }) {
  if (!steps?.length) return null;
  return (
    <div data-testid="issue-progress">
      <div className="mb-4 label-eyebrow text-npst-gold">Verified progress</div>
      <ol className="relative ml-1.5 space-y-4 border-l border-npst-goldBorder pl-5">
        {steps.map((s, i) => (
          <motion.li key={i} initial={{ opacity: 0, x: -8 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: 0.2 + i * 0.05 }} className="relative">
            <span className="absolute -left-[25px] top-1 h-2.5 w-2.5 rounded-full border-2 border-white bg-npst-gold ring-1 ring-npst-goldBorder" />
            <div className="font-mono text-[10px] text-npst-muted">{fmtDate(s.date)}</div>
            <div className="text-sm text-npst-ink">{s.label}</div>
          </motion.li>
        ))}
      </ol>
    </div>
  );
}

export default function IssueViewer({ slug, preview, onClose }) {
  const [d, setD] = useState(null);
  useEffect(() => {
    api.get(`/issues/${slug}`).then(({ data }) => setD(data)).catch(() => setD({ error: true }));
    const esc = (e) => { if (e.key === "Escape") onClose(); };
    window.addEventListener("keydown", esc);
    document.body.style.overflow = "hidden";
    return () => { window.removeEventListener("keydown", esc); document.body.style.overflow = ""; };
  }, [slug, onClose]);
  const issue = d && !d.error ? d : preview;
  const Icon = ISSUE_ICONS[issue?.icon] || Layers;
  const counts = Object.entries(issue?.counts || {});
  return (
    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} transition={{ duration: 0.3 }}
      className="fixed inset-0 z-[60] overflow-y-auto bg-npst-bg/95 backdrop-blur-xl" data-testid="issue-viewer-modal" role="dialog" aria-modal="true">
      <button onClick={onClose} data-testid="issue-viewer-close-button" aria-label="Close"
        className="fixed right-4 top-4 z-[70] rounded-full border border-npst-line bg-white p-3 text-npst-navy shadow-md transition-colors hover:border-npst-gold hover:text-npst-gold sm:right-8 sm:top-8"><X size={20} /></button>
      <div className="grid min-h-full grid-cols-1 lg:grid-cols-[1.2fr_1fr]">
        <motion.div initial={{ scale: 1.03, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} transition={{ duration: 0.7, ease: [0.215, 0.61, 0.355, 1] }}
          className="relative h-[48vh] overflow-hidden bg-npst-bgAlt lg:sticky lg:top-0 lg:h-screen">
          {issue && <IssueVisual issue={issue} thumb={false} />}
          {issue?.image_url && <AiBadge className="absolute left-5 top-5" />}
          {issue?.image_url && (
            <div className="absolute inset-x-5 bottom-5 flex items-center gap-2 rounded-xl border border-npst-line bg-white/90 px-3 py-2 font-mono text-[10px] text-npst-inkSoft backdrop-blur-md lg:right-auto" data-testid="issue-viewer-ai-note">
              <Info size={12} className="shrink-0 text-npst-gold" /> Illustrative AI-generated image — not a photograph or evidence.
            </div>
          )}
        </motion.div>
        <div className="space-y-9 px-5 pb-16 pt-8 sm:px-10 lg:pt-24">
          {issue && (
            <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }}>
              <div className="flex flex-wrap items-center gap-3 label-eyebrow text-npst-gold">
                <span className="inline-flex items-center gap-2"><Icon size={14} />{issue.category}</span>
                <span className="inline-flex items-center gap-2 rounded-full border border-npst-line bg-white px-2.5 py-1 font-sans normal-case tracking-normal text-[11px] font-medium text-npst-inkSoft" data-testid="issue-viewer-status">
                  <span className={`h-1.5 w-1.5 rounded-full ${STATUS_TONE[issue.status_kind] || "bg-npst-gold"}`} />{issue.status}
                </span>
              </div>
              <h2 className="mt-5 font-serif text-3xl sm:text-4xl lg:text-5xl font-semibold leading-tight text-npst-ink" data-testid="issue-viewer-title">{issue.title}</h2>
              <div className="mt-6 flex flex-wrap gap-2" data-testid="issue-viewer-chips">
                {counts.map(([k, n]) => {
                  const M = RECORD_META[k]; const I = M?.icon || Layers;
                  return <span key={k} className="inline-flex items-center gap-1.5 rounded-full bg-npst-bgAlt px-3 py-1.5 text-xs text-npst-inkSoft"><I size={12} className="text-npst-gold" />{n} {M?.label || k}</span>;
                })}
                {(d?.chips || []).map((c) => <span key={c} className="rounded-full border border-npst-goldBorder bg-npst-goldLight px-3 py-1.5 text-xs text-npst-navy">{c}</span>)}
              </div>
              <div className="mt-6"><IssueShare issue={issue} /></div>
            </motion.div>
          )}
          {!d && <div className="h-40 skeleton rounded-xl" />}
          {d?.error && <div className="text-sm text-npst-muted">This issue is no longer available.</div>}
          <Progress steps={d?.progress} />
          {d?.records?.length > 0 && (
            <div>
              <div className="mb-4 label-eyebrow text-npst-gold">Records</div>
              <div className={`grid grid-cols-1 gap-2.5 ${d.records.length > 2 ? "2xl:grid-cols-2" : ""}`} data-testid="issue-records-grid">{d.records.map((r, i) => <RecordTile key={i} r={r} i={i} />)}</div>
            </div>
          )}
        </div>
      </div>
    </motion.div>
  );
}
